import { refreshStaleStateOnce } from "_shared/_errors/stale-state";
import { api } from "_shared/_stores/api";
import { logSchemaFailure } from "_shared/_utilities/log-schema-failure";
import { z } from "zod";
import {
  ApproveProposalNegotiationCommandZ,
  CancelProposalNegotiationCommandZ,
  CompleteProposalReviewCommandZ,
  HireProposalNegotiationCommandZ,
  ProposalCollaborationEnvelopeZ,
  ProposalConflictResponseZ,
  ProposalTimelineResponseZ,
  RequestProposalReviewCommandZ,
  RequestProposalRevisionNegotiationCommandZ,
  ReplaceClientTeamMembersCommandZ,
  RejectProposalNegotiationCommandZ,
  SubmitProposalDraftCommandZ,
  type ApproveProposalNegotiationCommand,
  type CancelProposalNegotiationCommand,
  type CompleteProposalReviewCommand,
  type HireProposalNegotiationCommand,
  type ProposalCollaborationEnvelope,
  type ProposalTimelineResponse,
  type RequestProposalReviewCommand,
  type RequestProposalRevisionNegotiationCommand,
  type RejectProposalNegotiationCommand,
  type SaveProposalDraftCommand,
  type ReplaceClientTeamMembersCommand,
  type SubmitProposalDraftCommand,
} from "work/proposals/negotiation/proposal-collaboration-contract";
import {
  proposalChangesSeenLocally,
  proposalConflictsReported,
  proposalEnvelopeReceived,
  proposalSaveFailed,
  proposalSaveStarted,
} from "work/proposals/negotiation/proposal-collaboration-redux-slice";

const proposalCommandMediaType = "application/json";
export const proposalCollaborationTagId = (proposalId: string) => `COLLABORATION-${proposalId}`;

type ProposalCollaborationViewerArgument = {
  /** Part of the RTK cache identity; it is intentionally not sent to the API. */
  viewerScope: string;
};

/**
 * A command response is authoritative for the caller, but every other mounted
 * collaboration/timeline cache entry (the same leader's other tab, a second
 * proposal-id key after Submit) is now stale. Invalidate exactly the tags the
 * realtime notice already invalidates so a command and its notice behave
 * identically — a revoked reviewer edit right must not keep serving the
 * pre-command envelope to the actor who revoked it.
 *
 * Only a successful command invalidates. A rejected command has already
 * rebased Redux from its `409` envelope, and refetching on every transport
 * failure would turn an offline queue into a request loop.
 */
function invalidateCollaborationTags(result: ProposalCollaborationEnvelope | undefined) {
  if (!result) return [];

  const proposalId = result.projection.id;
  return [
    ...(proposalId ? [{ type: "Proposal" as const, id: proposalCollaborationTagId(proposalId) }] : []),
    { type: "Proposal" as const, id: "COLLABORATION" },
  ];
}

export const proposalCollaborationApi = api.injectEndpoints({
  endpoints: (build) => ({
    getProposalCollaboration: build.query<
      ProposalCollaborationEnvelope,
      { proposalId: string } & ProposalCollaborationViewerArgument
    >({
      query: ({ proposalId }) => `work/proposals/${proposalId}/negotiation`,
      rawResponseSchema: ProposalCollaborationEnvelopeZ,
      onSchemaFailure: logSchemaFailure,
      providesTags: (_result, _error, { proposalId }) => [
        { type: "Proposal", id: proposalCollaborationTagId(proposalId) },
        { type: "Proposal", id: "COLLABORATION" },
      ],
      onQueryStarted: async ({ viewerScope }, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled;
          dispatch(proposalEnvelopeReceived({ envelope: data, viewerScope }));
        } catch {
          // The existing authoritative envelope and local operation queue remain usable offline.
        }
      },
    }),
    getProposalTimeline: build.query<
      ProposalTimelineResponse,
      /**
       * `itemId` narrows a collection thread to one of its items — a fee-schedule category's own
       * history rather than the whole schedule's. It only means anything alongside a `path`, because
       * the proposal-wide channel is not a thread; the server ignores it without one.
       */
      { negotiationId: string; path?: string; itemId?: string } & ProposalCollaborationViewerArgument
    >({
      query: ({ negotiationId, path, itemId }) => {
        if (!path) return `work/proposal-negotiations/${negotiationId}/timeline`;
        const query = new URLSearchParams({ path });
        if (itemId) query.set("itemId", itemId);
        return `work/proposal-negotiations/${negotiationId}/timeline?${query}`;
      },
      rawResponseSchema: ProposalTimelineResponseZ,
      onSchemaFailure: logSchemaFailure,
      providesTags: [{ type: "Proposal", id: "COLLABORATION" }],
    }),
    saveProposalCollaborationDraft: build.mutation<
      ProposalCollaborationEnvelope,
      { negotiationId: string; command: SaveProposalDraftCommand } & ProposalCollaborationViewerArgument
    >({
      query: ({ negotiationId, command }) => ({
        url: `work/proposal-negotiations/${negotiationId}`,
        method: "PATCH",
        headers: { "Content-Type": proposalCommandMediaType },
        body: command,
      }),
      rawResponseSchema: ProposalCollaborationEnvelopeZ,
      onSchemaFailure: logSchemaFailure,
      invalidatesTags: (result) => invalidateCollaborationTags(result),
      onQueryStarted: async ({ negotiationId, command, viewerScope }, { dispatch, queryFulfilled }) => {
        dispatch(proposalSaveStarted({ negotiationId, command, viewerScope }));
        try {
          const { data } = await queryFulfilled;
          dispatch(proposalEnvelopeReceived({ envelope: data, viewerScope }));
        } catch (failure) {
          const conflictResponse = readConflictResponse(failure);
          const conflictRefresh = receiveLifecycleCommandConflict(
            failure,
            { negotiationId, viewerScope },
            dispatch,
            conflictResponse,
          );
          dispatch(
            proposalSaveFailed({
              negotiationId,
              commandId: command.commandId,
              viewerScope,
              uncertain: isUncertainTransportFailure(failure),
              conflicts: conflictResponse?.conflicts,
            }),
          );
          await conflictRefresh;
        }
      },
    }),
    replaceClientTeamMembers: build.mutation<
      ProposalCollaborationEnvelope,
      { negotiationId: string; command: ReplaceClientTeamMembersCommand } & ProposalCollaborationViewerArgument
    >({
      query: ({ negotiationId, command }) => ({
        url: `work/proposal-negotiations/${negotiationId}/client-team-members`,
        method: "PUT",
        headers: { "Content-Type": proposalCommandMediaType },
        body: ReplaceClientTeamMembersCommandZ.parse(command),
      }),
      rawResponseSchema: ProposalCollaborationEnvelopeZ,
      onSchemaFailure: logSchemaFailure,
      invalidatesTags: (result) => invalidateCollaborationTags(result),
      onQueryStarted: async ({ negotiationId, viewerScope }, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled;
          dispatch(proposalEnvelopeReceived({ envelope: data, viewerScope }));
        } catch (failure) {
          // The roster commits per action rather than through a save the actor can press again, so a
          // stale-cursor rejection has to heal itself: take the envelope the 409 carries, and the next
          // add or removal is prepared against the roster that actually won.
          await receiveLifecycleCommandConflict(failure, { negotiationId, viewerScope }, dispatch);
        }
      },
    }),
    submitProposalCollaborationDraft: build.mutation<
      ProposalCollaborationEnvelope,
      { negotiationId: string; command: SubmitProposalDraftCommand } & ProposalCollaborationViewerArgument
    >({
      query: ({ negotiationId, command }) => ({
        url: `work/proposal-negotiations/${negotiationId}/submit`,
        method: "POST",
        body: SubmitProposalDraftCommandZ.parse(command),
      }),
      rawResponseSchema: ProposalCollaborationEnvelopeZ,
      onSchemaFailure: logSchemaFailure,
      invalidatesTags: (result) => invalidateCollaborationTags(result),
      onQueryStarted: async ({ negotiationId, viewerScope }, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled;
          dispatch(proposalEnvelopeReceived({ envelope: data, viewerScope }));
        } catch (failure) {
          await receiveLifecycleCommandConflict(failure, { negotiationId, viewerScope }, dispatch);
        }
      },
    }),
    completeProposalReview: build.mutation<
      ProposalCollaborationEnvelope,
      { negotiationId: string; command: CompleteProposalReviewCommand } & ProposalCollaborationViewerArgument
    >({
      query: ({ negotiationId, command }) => ({
        url: `work/proposal-negotiations/${negotiationId}/complete-review`,
        method: "POST",
        body: CompleteProposalReviewCommandZ.parse(command),
      }),
      rawResponseSchema: ProposalCollaborationEnvelopeZ,
      onSchemaFailure: logSchemaFailure,
      invalidatesTags: (result) => invalidateCollaborationTags(result),
      onQueryStarted: async ({ negotiationId, viewerScope }, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled;
          dispatch(proposalEnvelopeReceived({ envelope: data, viewerScope }));
        } catch (failure) {
          await receiveLifecycleCommandConflict(failure, { negotiationId, viewerScope }, dispatch);
        }
      },
    }),
    /**
     * Fired the moment the leader clicks, deliberately NOT staged behind the reviewer popover's
     * Save. A re-review request is not proposal content: nothing about the proposal changes, so
     * there is nothing to stage, review, or discard alongside the roster edits.
     */
    requestProposalReReview: build.mutation<
      ProposalCollaborationEnvelope,
      { negotiationId: string; command: RequestProposalReviewCommand } & ProposalCollaborationViewerArgument
    >({
      query: ({ negotiationId, command }) => ({
        url: `work/proposal-negotiations/${negotiationId}/request-review`,
        method: "POST",
        body: RequestProposalReviewCommandZ.parse(command),
      }),
      rawResponseSchema: ProposalCollaborationEnvelopeZ,
      onSchemaFailure: logSchemaFailure,
      invalidatesTags: (result) => invalidateCollaborationTags(result),
      onQueryStarted: async ({ negotiationId, viewerScope }, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled;
          dispatch(proposalEnvelopeReceived({ envelope: data, viewerScope }));
        } catch (failure) {
          await receiveLifecycleCommandConflict(failure, { negotiationId, viewerScope }, dispatch);
        }
      },
    }),
    requestProposalRevisionNegotiation: build.mutation<
      ProposalCollaborationEnvelope,
      { negotiationId: string; command: RequestProposalRevisionNegotiationCommand } & ProposalCollaborationViewerArgument
    >({
      query: ({ negotiationId, command }) => ({
        url: `work/proposal-negotiations/${negotiationId}/request-revision`,
        method: "POST",
        body: RequestProposalRevisionNegotiationCommandZ.parse(command),
      }),
      rawResponseSchema: ProposalCollaborationEnvelopeZ,
      onSchemaFailure: logSchemaFailure,
      invalidatesTags: (result) => invalidateCollaborationTags(result),
      onQueryStarted: async ({ negotiationId, viewerScope }, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled;
          dispatch(proposalEnvelopeReceived({ envelope: data, viewerScope }));
        } catch (failure) {
          await receiveLifecycleCommandConflict(failure, { negotiationId, viewerScope }, dispatch);
        }
      },
    }),
    approveProposalNegotiation: build.mutation<
      ProposalCollaborationEnvelope,
      { negotiationId: string; command: ApproveProposalNegotiationCommand } & ProposalCollaborationViewerArgument
    >({
      query: ({ negotiationId, command }) => ({
        url: `work/proposal-negotiations/${negotiationId}/approve`,
        method: "POST",
        body: ApproveProposalNegotiationCommandZ.parse(command),
      }),
      rawResponseSchema: ProposalCollaborationEnvelopeZ,
      onSchemaFailure: logSchemaFailure,
      invalidatesTags: (result) => invalidateCollaborationTags(result),
      onQueryStarted: async ({ negotiationId, viewerScope }, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled;
          dispatch(proposalEnvelopeReceived({ envelope: data, viewerScope }));
        } catch (failure) {
          await receiveLifecycleCommandConflict(failure, { negotiationId, viewerScope }, dispatch);
        }
      },
    }),
    rejectProposalNegotiation: build.mutation<
      ProposalCollaborationEnvelope,
      { negotiationId: string; command: RejectProposalNegotiationCommand } & ProposalCollaborationViewerArgument
    >({
      query: ({ negotiationId, command }) => ({
        url: `work/proposal-negotiations/${negotiationId}/reject`,
        method: "POST",
        body: RejectProposalNegotiationCommandZ.parse(command),
      }),
      rawResponseSchema: ProposalCollaborationEnvelopeZ,
      onSchemaFailure: logSchemaFailure,
      invalidatesTags: (result) => invalidateCollaborationTags(result),
      onQueryStarted: async ({ negotiationId, viewerScope }, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled;
          dispatch(proposalEnvelopeReceived({ envelope: data, viewerScope }));
        } catch (failure) {
          await receiveLifecycleCommandConflict(failure, { negotiationId, viewerScope }, dispatch);
        }
      },
    }),
    cancelProposalNegotiation: build.mutation<
      ProposalCollaborationEnvelope,
      { negotiationId: string; command: CancelProposalNegotiationCommand } & ProposalCollaborationViewerArgument
    >({
      query: ({ negotiationId, command }) => ({
        url: `work/proposal-negotiations/${negotiationId}/cancel`,
        method: "POST",
        body: CancelProposalNegotiationCommandZ.parse(command),
      }),
      rawResponseSchema: ProposalCollaborationEnvelopeZ,
      onSchemaFailure: logSchemaFailure,
      invalidatesTags: (result) => invalidateCollaborationTags(result),
      onQueryStarted: async ({ negotiationId, viewerScope }, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled;
          dispatch(proposalEnvelopeReceived({ envelope: data, viewerScope }));
        } catch (failure) {
          await receiveLifecycleCommandConflict(failure, { negotiationId, viewerScope }, dispatch);
        }
      },
    }),
    hireProposalNegotiation: build.mutation<
      ProposalCollaborationEnvelope,
      { negotiationId: string; command: HireProposalNegotiationCommand } & ProposalCollaborationViewerArgument
    >({
      query: ({ negotiationId, command }) => ({
        url: `work/proposal-negotiations/${negotiationId}/hire`,
        method: "POST",
        body: HireProposalNegotiationCommandZ.parse(command),
      }),
      rawResponseSchema: ProposalCollaborationEnvelopeZ,
      onSchemaFailure: logSchemaFailure,
      invalidatesTags: (result) => invalidateCollaborationTags(result),
      onQueryStarted: async ({ negotiationId, viewerScope }, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled;
          dispatch(proposalEnvelopeReceived({ envelope: data, viewerScope }));
        } catch (failure) {
          await receiveLifecycleCommandConflict(failure, { negotiationId, viewerScope }, dispatch);
        }
      },
    }),
    /**
     * Advances the viewer's per-path seen cursor (an omitted/"" path is the whole proposal). The 204
     * carries no body, so nothing is parsed. The envelope is not returned, so this cannot reuse
     * invalidateCollaborationTags (which keys off the result's proposal id); it invalidates the shared
     * COLLABORATION tag to refetch the envelope with authoritative cursors, and optimistically bumps the
     * local cursor first so the cleared count shows immediately.
     */
    markProposalChangesSeen: build.mutation<
      void,
      { negotiationId: string; path?: string; throughStreamVersion: number }
    >({
      query: ({ negotiationId, path, throughStreamVersion }) => ({
        url: `work/proposal-negotiations/${negotiationId}/seen`,
        method: "POST",
        body: { path: path ?? "", throughStreamVersion },
      }),
      invalidatesTags: [{ type: "Proposal", id: "COLLABORATION" }],
      onQueryStarted: async ({ negotiationId, path, throughStreamVersion }, { dispatch, queryFulfilled }) => {
        dispatch(proposalChangesSeenLocally({ negotiationId, path: path ?? "", throughStreamVersion }));
        try {
          await queryFulfilled;
        } catch {
          // The envelope refetch (or the next realtime notice) restores the authoritative cursor.
        }
      },
    }),
  }),
});

function readConflictResponse(failure: unknown) {
  if (!isRecord(failure) || !isRecord(failure.error)) return undefined;
  const parsed = ProposalConflictResponseZ.safeParse(failure.error.data);
  return parsed.success ? parsed.data : undefined;
}

/**
 * Reads the structured `409` body off a rejected mutation so a caller can tell
 * the user *which* fields collided instead of "the command failed".
 *
 * The rejection value RTK Query hands a `.unwrap()` caller is the bare
 * `error` object, while `onQueryStarted` receives it wrapped as `{ error }`.
 * Accept both so one reader serves the endpoint and the page.
 */
export function readProposalConflictResponse(failure: unknown) {
  return readConflictResponse(failure) ?? readConflictResponse({ error: failure });
}

/** The `ProblemDetails` body a rejected command carries: prose, plus per-field reasons when it has them. */
const ProposalCommandProblemZ = z.object({
  detail: z.string().min(1).nullish(),
  validationErrors: z.array(z.string()).nullish(),
});

function readCommandProblem(failure: unknown): string | undefined {
  if (!isRecord(failure) || !isRecord(failure.error)) return undefined;
  const parsed = ProposalCommandProblemZ.safeParse(failure.error.data);
  if (!parsed.success) return undefined;

  // Per-field reasons read better as their own sentence list than as the prose that already contains
  // them; the prose is the fallback for a rejection that carries no field detail at all.
  const { detail, validationErrors } = parsed.data;
  if (validationErrors && validationErrors.length > 0) return validationErrors.join(" ");
  return detail ?? undefined;
}

/**
 * Reads why a command was rejected, for the cases the `409` conflict reader does not cover.
 *
 * A submit whose draft fails validation returns a `400` whose reasons live in `ProblemDetails`, and the
 * pages reported a bare "Failed to submit proposal." because nothing read that body. Since the invalid
 * content is persisted, the same submit failed identically after a refresh with nothing on screen
 * naming the field to fix — a dead end that looked like a transport bug.
 *
 * Accepts both rejection shapes for the same reason {@link readProposalConflictResponse} does.
 */
export function readProposalCommandErrorDetail(failure: unknown): string | undefined {
  return readCommandProblem(failure) ?? readCommandProblem({ error: failure });
}

/**
 * A rejected lifecycle command carries the same authoritative envelope a
 * successful one would, so Redux rebases onto it either way.
 *
 * The explicit conflict dispatch is not redundant with the envelope: the
 * envelope is only adopted if it wins `receiveProposalEnvelope`'s
 * `streamVersion` guard, and a realtime notice that landed while the command
 * was in flight can push local state past the 409's snapshot. The reported
 * conflicts are authoritative regardless, so they are recorded separately —
 * and only when non-empty, leaving the pure stale-base rebase untouched.
 */
async function receiveLifecycleCommandConflict(
  failure: unknown,
  context: { negotiationId: string; viewerScope: string },
  dispatch: (action: ReturnType<typeof proposalEnvelopeReceived | typeof proposalConflictsReported>) => unknown,
  conflictResponse = readConflictResponse(failure),
) {
  if (!conflictResponse) return undefined;

  const { negotiationId, viewerScope } = context;
  const error = isRecord(failure) && "error" in failure ? failure.error : failure;
  await refreshStaleStateOnce(error, `proposal-negotiation:${negotiationId}`, () => {
    // ProposalConflictResponse remains the one proposal-specific source of truth. The shared helper
    // owns only once-per-visible-conflict refresh orchestration and never interprets this envelope.
    dispatch(proposalEnvelopeReceived({ envelope: conflictResponse.envelope, viewerScope }));
    if (conflictResponse.conflicts.length > 0) {
      dispatch(proposalConflictsReported({ negotiationId, viewerScope, conflicts: conflictResponse.conflicts }));
    }
  });
  return conflictResponse;
}

function isUncertainTransportFailure(failure: unknown): boolean {
  if (!isRecord(failure) || !isRecord(failure.error)) return false;
  return failure.error.status === "FETCH_ERROR" || failure.error.status === "TIMEOUT_ERROR";
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

export const {
  useGetProposalCollaborationQuery,
  useLazyGetProposalCollaborationQuery,
  useGetProposalTimelineQuery,
  useLazyGetProposalTimelineQuery,
  useSaveProposalCollaborationDraftMutation,
  useReplaceClientTeamMembersMutation,
  useSubmitProposalCollaborationDraftMutation,
  useRequestProposalRevisionNegotiationMutation,
  useCompleteProposalReviewMutation,
  useRequestProposalReReviewMutation,
  useApproveProposalNegotiationMutation,
  useRejectProposalNegotiationMutation,
  useCancelProposalNegotiationMutation,
  useHireProposalNegotiationMutation,
  useMarkProposalChangesSeenMutation,
} = proposalCollaborationApi;
